import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useConfirm } from "../ConfirmDialog.js";
import { useToast } from "../Toast.js";
import { composeResetConfirmCopy } from "../../lib/watchTracking.js";
import { ErrorState, LoadingState } from "../States.js";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch, ApiRequestError } from "../../lib/api.js";
import { cronToText } from "../../lib/cronDisplay.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";
import type { MemberCandidate } from "../MemberPicker.js";
import type { ActionItemCode } from "../../lib/actionItems.js";
import type { EscalationPolicy } from "../../lib/escalation.js";
import type { TenantCustomerRoleType } from "../../lib/customers.js";
import {
  ActionItemResponseForm,
  EmailResponseForm,
  WebhookResponseForm,
  makeResponseConfig,
  summarizeResponse,
  type ActionItemResponseConfig,
  type EmailResponseConfig,
  type ResponseConfig,
  type ResponseType,
  type WebhookResponseConfig
} from "./WatchResponseForms.js";

/**
 * Watch node inspector (ADR-0042 — 2026-04-24 row-level refactor).
 *
 * UX model:
 *  - Two trigger shapes: "Match condition" (backend row_match) and
 *    "Change detection" (backend row_delta_pct_gt | row_delta_abs_gt with a
 *    %-vs-absolute unit toggle).
 *  - Every column reference is a bound selection from the upstream node's
 *    output_schema. No free-text column entry anywhere.
 */

type RowMatchOp = "gt" | "lt" | "gte" | "lte" | "eq" | "neq";
type BackendTriggerType = "row_any" | "row_match" | "row_delta_pct_gt" | "row_delta_abs_gt";
type TriggerKind = "any" | "match" | "change";
type ChangeUnit = "percent" | "absolute";

const OP_OPTIONS: Array<{ value: RowMatchOp; label: string }> = [
  { value: "gt", label: ">" },
  { value: "lt", label: "<" },
  { value: "gte", label: "≥" },
  { value: "lte", label: "≤" },
  { value: "eq", label: "=" },
  { value: "neq", label: "≠" }
];

interface WatchConfig {
  trigger?: {
    type?: BackendTriggerType;
    column?: string;
    op?: RowMatchOp;
    threshold?: number | { column: string };
  };
  identifying_columns?: string[];
  title_template?: string;
  description_template?: string;
  sustained_for_minutes?: number;
  responses?: ResponseConfig[];
  /** ADR-0075 §4.9 — dynamic user mapping for action item slots. */
  dynamic_user_mapping_enabled?: boolean;
  customer_column?: string | null;
}

interface DryRunResult {
  matched_rows: Array<{
    identity_hash: string;
    identifying_values: Record<string, unknown>;
    matches: boolean;
    would_fire: boolean;
    trigger_context: Record<string, unknown>;
  }>;
  duplicate_identities_warning: string[];
  /** Rendered would-fire previews (2026-08-17): what a fire would produce —
   *  interpolated templates, resolved recipients, the exact delivery email. */
  previews?: Array<{
    identity_hash: string;
    identifying_values: Record<string, unknown>;
    responses: Array<{
      response_id: string;
      response_type: string;
      previewable: boolean;
      note?: string;
      title?: string;
      severity?: string;
      item_type_label?: string;
      recipients?: Array<{ name: string; roles: string[] }>;
      email_subject?: string;
      email_body?: string;
    }>;
  }>;
  test_email?: { sent_to: string; sent: number; skipped: number };
}

interface ScheduledWorkflow {
  id: string;
  name: string;
  schedule_status: string | null;
  schedule_cron: string | null;
}

interface InputSchema {
  columns: Array<{ name: string; type: string }>;
  source_node_id: string | null;
  last_executed_at: string | null;
  stale: boolean;
  reason: string | null;
}

/** ADR-0168 — what the watch is currently tracking, and the reset affordance. */
interface TrackedRow {
  identity_hash: string;
  identifying_values: Record<string, unknown>;
  first_true_at: string | null;
  last_matched_at: string | null;
  last_fired_at: string | null;
}

interface RowStateResponse {
  summary: { tracked: number; matching: number; fired: number };
  currently_matching: TrackedRow[];
}

export function OperationInspectorWatch({
  selectedNode,
  operationId,
  operationName,
  setNodeData,
  flushAutoSave
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  /** Operation name from the editor — used as the seed for the new schedule's
   *  workflow name when the user clicks "Create schedule" on the no-schedule
   *  banner. */
  operationName: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  /** Cancels any pending debounced autosave and saves synchronously if dirty.
   *  Awaited before dry-run so the server sees the latest config. */
  flushAutoSave: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const config: WatchConfig = (selectedNode.data.config as WatchConfig) ?? {};
  const trigger = config.trigger ?? { type: "row_match", column: "", op: "gt" as RowMatchOp, threshold: 0 };
  const backendType: BackendTriggerType = trigger.type ?? "row_match";
  const triggerKind: TriggerKind =
    backendType === "row_any" ? "any" : backendType === "row_match" ? "match" : "change";
  const changeUnit: ChangeUnit = backendType === "row_delta_abs_gt" ? "absolute" : "percent";
  const responses: ResponseConfig[] = config.responses ?? [];

  const [busy, setBusy] = useState(false);
  const [dryRunError, setDryRunError] = useState<string | null>(null);
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);

  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [itemTypes, setItemTypes] = useState<ActionItemCode[]>([]);
  const [policies, setPolicies] = useState<EscalationPolicy[]>([]);
  const [extraLoadError, setExtraLoadError] = useState<string | null>(null);

  const [schedules, setSchedules] = useState<ScheduledWorkflow[]>([]);
  const [schedulesLoaded, setSchedulesLoaded] = useState(false);
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  const [createScheduleError, setCreateScheduleError] = useState<string | null>(null);

  const [schema, setSchema] = useState<InputSchema | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // ADR-0168 — tracking state + reset.
  const confirm = useConfirm();
  const toast = useToast();
  const [tracking, setTracking] = useState<RowStateResponse | null>(null);
  const [trackingLoading, setTrackingLoading] = useState(true);
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [resetBusy, setResetBusy] = useState(false);

  const loadTracking = useCallback(async () => {
    setTrackingLoading(true);
    setTrackingError(null);
    try {
      const res = await apiFetch<RowStateResponse>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/watch-nodes/${encodeURIComponent(selectedNode.id)}/row-state`
      );
      setTracking(res);
    } catch (e) {
      setTrackingError(
        e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
      );
    } finally {
      setTrackingLoading(false);
    }
  }, [operationId, selectedNode.id]);

  useEffect(() => {
    void loadTracking();
  }, [loadTracking]);

  /**
   * The columns to show for tracked rows. Taken from the ROWS, not from the
   * current config: `identifying_values` is frozen at the moment an identity was
   * first tracked, so after an identifying-columns edit the stored rows and the
   * config genuinely disagree — and what the table must show is what the watch
   * is actually tracking. Falls back to the config when nothing is tracked yet.
   */
  const identifyingColumns = useMemo(() => {
    const seen: string[] = [];
    for (const row of tracking?.currently_matching ?? []) {
      for (const key of Object.keys(row.identifying_values ?? {})) {
        if (!seen.includes(key)) seen.push(key);
      }
    }
    return seen.length > 0 ? seen : (config.identifying_columns ?? []);
  }, [tracking, config.identifying_columns]);

  async function resetTracking(row: TrackedRow | null) {
    const ok = await confirm.confirm({
      title: row ? "Reset tracking for this row?" : "Reset tracking for this watch?",
      detail: composeResetConfirmCopy({
        trigger,
        sustainedForMinutes: config.sustained_for_minutes,
        responses,
        scope: row ? "identity" : "watch",
        counts: tracking?.summary ?? { tracked: 0, matching: 0 },
        identifyingValues: row?.identifying_values ?? null
      }),
      confirmLabel: "Reset tracking",
      destructive: true
    });
    if (!ok) return;
    setResetBusy(true);
    try {
      const res = await apiFetch<{ cleared: number; scope: string }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/watch-nodes/${encodeURIComponent(selectedNode.id)}/tracking-reset`,
        { method: "POST", body: row ? { identity_hash: row.identity_hash } : {} }
      );
      toast.push({
        kind: "success",
        message:
          res.cleared === 0
            ? "Nothing was being tracked."
            : `Cleared tracking for ${res.cleared} ${res.cleared === 1 ? "row" : "rows"}.`
      });
      await loadTracking();
    } catch (e) {
      toast.push({
        kind: "error",
        message: e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
      });
    } finally {
      setResetBusy(false);
    }
  }

  // ADR-0075 — load tenant role types, customers, and default-coverage
  // mappings for the dynamic-mapping validation summary. Default coverage
  // replaced the old "catchall customer" — the resolver falls back here when
  // a row's account_code doesn't match any customer.
  const [customerRoleTypes, setCustomerRoleTypes] = useState<TenantCustomerRoleType[]>([]);
  const [tenantHasCustomers, setTenantHasCustomers] = useState<boolean>(true);
  const [defaultHasPrimary, setDefaultHasPrimary] = useState<boolean>(true);
  useEffect(() => {
    void (async () => {
      try {
        const [rt, cs, def] = await Promise.all([
          apiFetch<{ role_types: TenantCustomerRoleType[] }>("/api/tenant/admin/customer-role-types"),
          apiFetch<{ customers: Array<{ id: string }> }>("/api/tenant/admin/customers"),
          apiFetch<{ mappings: Array<{ is_primary: boolean }> }>("/api/tenant/admin/customers/default-coverage")
        ]);
        setCustomerRoleTypes(rt.role_types ?? []);
        setTenantHasCustomers((cs.customers ?? []).length > 0);
        setDefaultHasPrimary((def.mappings ?? []).some((m) => m.is_primary));
      } catch {
        // Tenant non-admins can't hit these — leave defaults; warnings will
        // surface in the validation summary below.
      }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const [dir, types, pols] = await Promise.all([
          apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates"),
          apiFetch<{ codes: ActionItemCode[] }>("/api/tenant/action-item-codes/item-types"),
          apiFetch<{ policies: EscalationPolicy[] }>("/api/tenant/escalation-policies")
        ]);
        setCandidates(dir.candidates);
        setItemTypes(types.codes);
        setPolicies(pols.policies);
      } catch (e) {
        setExtraLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiFetch<{ workflows: ScheduledWorkflow[] }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/schedules`
        );
        setSchedules(res.workflows);
      } catch {
        setSchedules([]);
      } finally {
        setSchedulesLoaded(true);
      }
    })();
  }, [operationId, selectedNode.id]);

  useEffect(() => {
    void (async () => {
      setSchemaError(null);
      try {
        const res = await apiFetch<InputSchema>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/input-schema`
        );
        setSchema(res);
      } catch (e) {
        setSchemaError(e instanceof Error ? e.message : String(e));
        setSchema(null);
      }
    })();
  }, [operationId, selectedNode.id]);

  async function refreshSchema() {
    try {
      const res = await apiFetch<InputSchema>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/input-schema`
      );
      setSchema(res);
      setSchemaError(null);
    } catch (e) {
      setSchemaError(e instanceof Error ? e.message : String(e));
    }
  }

  function updateConfig(patch: Partial<WatchConfig>) {
    setNodeData(selectedNode.id, (data) => ({
      ...data,
      config: { ...(data.config as object), ...patch }
    }));
  }

  function updateTrigger(patch: Partial<WatchConfig["trigger"]>) {
    updateConfig({ trigger: { ...trigger, ...(patch as object) } });
  }

  function setResponses(next: ResponseConfig[]) {
    updateConfig({ responses: next });
  }

  function addResponse(kind: ResponseType) {
    setResponses([...responses, makeResponseConfig(kind)]);
  }

  async function createScheduleForThisWatch() {
    setCreatingSchedule(true);
    setCreateScheduleError(null);
    try {
      // Make sure any pending edits land before we wire the new workflow up
      // — the task creation reads the saved spec via op_node_id.
      await flushAutoSave();
      const watchLabel = selectedNode.data.label?.trim() || "Watch";
      const workflowName = `${operationName.trim() || "Operation"} — ${watchLabel}`;
      const wfRes = await apiFetch<{ workflow: { id: string } }>(
        "/api/tenant/workflows",
        {
          method: "POST",
          body: { name: workflowName, scope: "personal", failure_mode: "stop" }
        }
      );
      const newWorkflowId = wfRes.workflow.id;
      await apiFetch(
        `/api/tenant/workflows/${encodeURIComponent(newWorkflowId)}/tasks`,
        {
          method: "POST",
          body: {
            name: watchLabel,
            type: "operation",
            operation_id: operationId,
            op_node_id: selectedNode.id,
            position_x: 50,
            position_y: 50
          }
        }
      );
      navigate(`/app/schedule/workflows/${encodeURIComponent(newWorkflowId)}`);
    } catch (e) {
      setCreateScheduleError(
        e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
      );
    } finally {
      setCreatingSchedule(false);
    }
  }

  async function runDryRun(sendTest = false) {
    setBusy(true);
    setDryRunError(null);
    if (!sendTest) setDryRunResult(null);
    try {
      // The dry-run reads watch config from the saved operation spec. Flush
      // any pending autosave first so in-flight edits (e.g., a just-changed
      // identifying_columns set) reach the DB before evaluation.
      await flushAutoSave();
      const res = await apiFetch<DryRunResult>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/evaluate`,
        { method: "POST", body: { watch_node_id: selectedNode.id, ...(sendTest ? { send_test_email: true } : {}) } }
      );
      setDryRunResult(res);
      if (res.test_email) {
        const { sent, skipped } = res.test_email;
        toast.push({
          kind: "success",
          message:
            `Sent ${sent} test email${sent === 1 ? "" : "s"} to you.` +
            (skipped > 0 ? ` ${skipped} more would fire — capped at ${sent}.` : "")
        });
      } else if (sendTest) {
        toast.push({ kind: "success", message: "Nothing would fire, so there was nothing to send." });
      }
    } catch (e) {
      setDryRunError(
        e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
      );
    } finally {
      setBusy(false);
    }
  }

  function toggleIdentifyingColumn(name: string, checked: boolean) {
    const current = config.identifying_columns ?? [];
    const next = checked ? [...current, name] : current.filter((c) => c !== name);
    updateConfig({ identifying_columns: next });
  }

  function onChangeTriggerKind(nextKind: TriggerKind) {
    if (nextKind === "any") {
      updateConfig({ trigger: { type: "row_any" } });
    } else if (nextKind === "match") {
      updateConfig({
        trigger: { type: "row_match", column: trigger.column ?? "", op: "gt", threshold: 0 }
      });
    } else {
      updateConfig({
        trigger: { type: "row_delta_pct_gt", column: trigger.column ?? "", threshold: 0 },
        sustained_for_minutes: undefined
      });
    }
  }

  function onChangeUnit(nextUnit: ChangeUnit) {
    updateTrigger({ type: nextUnit === "absolute" ? "row_delta_abs_gt" : "row_delta_pct_gt" });
  }

  const thresholdIsColumn =
    typeof trigger.threshold === "object" && trigger.threshold !== null && "column" in trigger.threshold;

  const columns = schema?.columns ?? [];
  const selectedIdentifyingSet = useMemo(
    () => new Set(config.identifying_columns ?? []),
    [config.identifying_columns]
  );

  /** Set of valid interpolation keys (lowercased). Server-side interpolation
   *  is case-insensitive, so we match the same way here. Reserved keys
   *  (operation_name, evaluated_at) are always available. */
  const validInterpolationKeys = useMemo(() => {
    const set = new Set<string>(["operation_name", "evaluated_at"]);
    for (const c of columns) set.add(c.name.toLowerCase());
    return set;
  }, [columns]);

  const titleUnresolved = useMemo(
    () => unresolvedTemplateRefs(config.title_template ?? "", validInterpolationKeys),
    [config.title_template, validInterpolationKeys]
  );
  const descriptionUnresolved = useMemo(
    () => unresolvedTemplateRefs(config.description_template ?? "", validInterpolationKeys),
    [config.description_template, validInterpolationKeys]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <InspectorTabs
        surfaceId="operationEditor"
        tabGroupId="watch"
        defaultSlot="setup"
        tabs={[
          {
            slot: "info",
            content: (
              <div className="field">
                <label className="label">Node label</label>
                <input
                  className="input"
                  value={selectedNode.data.label}
                  onChange={(e) => setNodeData(selectedNode.id, (d) => ({ ...d, label: e.target.value }))}
                  onBlur={() => void flushAutoSave()}
                  placeholder="Unnamed"
                />
              </div>
            ),
          },
          {
            slot: "setup",
            content: (
    <div style={{ display: "grid", gap: 16 }}>
      {/* ── Schedule banner ────────────────────────────────────── */}
      {schedulesLoaded && schedules.length === 0 && (
        <div className="card" style={{ borderColor: "var(--danger-border, #e0b4b4)", background: "var(--danger-bg, #fff5f5)" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>No schedule attached</div>
          <div className="help" style={{ marginBottom: 8 }}>
            This watch will never evaluate until a Schedule is created for it. Watches run when the Atlas
            Schedules engine fires them.
          </div>
          <button
            type="button"
            className="btn btnPrimary btnSm"
            onClick={() => void createScheduleForThisWatch()}
            disabled={creatingSchedule}
          >
            {creatingSchedule ? "Creating…" : "Create schedule"}
          </button>
          {createScheduleError && (
            <div className="help" style={{ color: "var(--danger-text)", marginTop: 6 }}>
              {createScheduleError}
            </div>
          )}
        </div>
      )}
      {schedulesLoaded && schedules.length > 0 && (
        <div className="card" style={{ background: "var(--bg-subtle)" }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Scheduled in:</div>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
            {schedules.map((w) => (
              <li key={w.id}>
                <Link to={`/app/schedule/workflows/${w.id}`}>{w.name}</Link>
                {" "}
                {w.schedule_status === "active" ? (
                  <span className="pill pillOk">active</span>
                ) : (
                  <span className="pill pillBad">{w.schedule_status ?? "paused"}</span>
                )}
                {w.schedule_cron && <span className="muted"> · {cronToText(w.schedule_cron)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Schema-unknown banner ──────────────────────────────── */}
      {schema && schema.stale && (
        <div className="card" style={{ background: "var(--bg-subtle)" }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Upstream schema not available yet</div>
          <div className="help" style={{ marginBottom: 8 }}>
            {schema.reason === "no_upstream_edge"
              ? "This watch has no upstream data edge. Connect a provider or transform node to the watch's tabular input before configuring columns."
              : "No cached output found for the upstream node. Run the upstream provider or transform once (from the canvas) to populate the schema, then reopen this inspector."}
          </div>
          <button className="btn btnSm" type="button" onClick={() => void refreshSchema()}>
            Refresh schema
          </button>
        </div>
      )}
      {schemaError && (
        <div className="help" style={{ color: "var(--danger-text)" }}>
          Could not load upstream schema: {schemaError}
        </div>
      )}

      {/* ── Identity ───────────────────────────────────────────── */}
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 700 }}>Identity</div>
        <div className="help">
          Each unique combination of these columns is treated as a distinct trackable identity — one
          action item per matching identity. The SQL author is responsible for uniqueness; non-unique
          identities are flagged in dry-run but not rejected.
        </div>

        <div className="field">
          <span className="label">Identifying columns *</span>
          {columns.length === 0 ? (
            <div className="help muted">No upstream columns available yet.</div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 6,
                border: "1px solid var(--border)",
                padding: 8,
                borderRadius: 4
              }}
            >
              {columns.map((col) => (
                <label
                  key={col.name}
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIdentifyingSet.has(col.name)}
                    onChange={(e) => toggleIdentifyingColumn(col.name, e.target.checked)}
                  />
                  <span className="mono">{col.name}</span>
                  <span className="muted" style={{ fontSize: 11 }}>· {col.type}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <label className="field">
          <span className="label">Action item title *</span>
          <input
            className="input"
            value={config.title_template ?? ""}
            onChange={(e) => updateConfig({ title_template: e.target.value })}
            placeholder="Overrun on {{machine_name}} ({{operator_name}})"
          />
          <div className="help" style={{ marginTop: 4 }}>
            Interpolated per matching row. Use <span className="mono">{"{{column_name}}"}</span> to pull
            any column value (case-insensitive); <span className="mono">{"{{operation_name}}"}</span>{" "}
            and <span className="mono">{"{{evaluated_at}}"}</span> also available.
          </div>
          {titleUnresolved.length > 0 && columns.length > 0 && (
            <div className="help" style={{ color: "var(--danger-text)", marginTop: 4 }}>
              These references won't resolve and will render as literal text:{" "}
              {titleUnresolved.map((k, i) => (
                <span key={k} className="mono">
                  {i > 0 ? ", " : ""}
                  {`{{${k}}}`}
                </span>
              ))}
            </div>
          )}
        </label>

        <label className="field">
          <span className="label">Description template</span>
          <textarea
            className="input"
            rows={3}
            value={config.description_template ?? ""}
            onChange={(e) => updateConfig({ description_template: e.target.value })}
            placeholder="Job {{operation_name}} for {{customer_name}} ran {{actual_minutes}} min vs {{expected_minutes}}."
          />
          <div className="help" style={{ marginTop: 4 }}>
            Shown on the resulting action item. Same interpolation surface as the title.
          </div>
          {descriptionUnresolved.length > 0 && columns.length > 0 && (
            <div className="help" style={{ color: "var(--danger-text)", marginTop: 4 }}>
              These references won't resolve and will render as literal text:{" "}
              {descriptionUnresolved.map((k, i) => (
                <span key={k} className="mono">
                  {i > 0 ? ", " : ""}
                  {`{{${k}}}`}
                </span>
              ))}
            </div>
          )}
        </label>
      </div>

      {/* ── Trigger ────────────────────────────────────────────── */}
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 700 }}>Trigger</div>

        <label className="field">
          <span className="label">Type *</span>
          <select
            className="select"
            value={triggerKind}
            onChange={(e) => onChangeTriggerKind(e.target.value as TriggerKind)}
          >
            <option value="any">Any returned row — every row fires</option>
            <option value="match">Match condition — evaluate each row</option>
            <option value="change">Change detection — alert when a column's value moves</option>
          </select>
          {triggerKind === "any" && (
            <div className="help" style={{ marginTop: 4 }}>
              Every row returned by the upstream query is a firing identity. Filter in the query — if a
              row comes through, an action item is created for its identity. Duplicate-suppression
              against open items is automatic.
            </div>
          )}
        </label>

        {triggerKind !== "any" && (
          <label className="field">
            <span className="label">Column *</span>
            {columns.length === 0 ? (
              <div className="help muted">No upstream columns available yet.</div>
            ) : (
              <select
                className="select"
                value={trigger.column ?? ""}
                onChange={(e) => updateTrigger({ column: e.target.value })}
              >
                <option value="">Select…</option>
                {columns.map((col) => (
                  <option key={col.name} value={col.name}>
                    {col.name} · {col.type}
                  </option>
                ))}
              </select>
            )}
          </label>
        )}

        {triggerKind === "match" && (
          <>
            <label className="field">
              <span className="label">Operator *</span>
              <select
                className="select"
                value={trigger.op ?? "gt"}
                onChange={(e) => updateTrigger({ op: e.target.value as RowMatchOp })}
              >
                {OP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>

            <div className="field">
              <span className="label">Threshold *</span>
              <div style={{ display: "grid", gap: 8 }}>
                <select
                  className="select"
                  value={thresholdIsColumn ? "column" : "number"}
                  onChange={(e) => {
                    if (e.target.value === "column") updateTrigger({ threshold: { column: "" } });
                    else updateTrigger({ threshold: 0 });
                  }}
                >
                  <option value="number">Compare to a number</option>
                  <option value="column">Compare to another column on the same row</option>
                </select>
                {thresholdIsColumn ? (
                  columns.length === 0 ? (
                    <div className="help muted">No upstream columns available yet.</div>
                  ) : (
                    <select
                      className="select"
                      value={(trigger.threshold as { column: string }).column}
                      onChange={(e) => updateTrigger({ threshold: { column: e.target.value } })}
                    >
                      <option value="">Select column…</option>
                      {columns.map((col) => (
                        <option key={col.name} value={col.name}>
                          {col.name} · {col.type}
                        </option>
                      ))}
                    </select>
                  )
                ) : (
                  <input
                    type="number"
                    className="input"
                    value={typeof trigger.threshold === "number" ? trigger.threshold : 0}
                    onChange={(e) => updateTrigger({ threshold: Number(e.target.value) })}
                  />
                )}
              </div>
            </div>
          </>
        )}

        {triggerKind !== "change" && (
          <label className="field">
            <span className="label">Sustained for (minutes)</span>
            <input
              type="number"
              className="input"
              min={0}
              value={config.sustained_for_minutes ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === "" ? undefined : Number(raw);
                updateConfig({ sustained_for_minutes: Number.isFinite(n) ? (n as number) : undefined });
              }}
              placeholder="e.g. 30"
            />
            <div className="help" style={{ marginTop: 4 }}>
              Per-identity debounce — the condition must hold continuously for this many minutes on
              that identity before firing.
            </div>
          </label>
        )}

        {triggerKind === "change" && (
          <>
            <label className="field">
              <span className="label">Unit *</span>
              <select
                className="select"
                value={changeUnit}
                onChange={(e) => onChangeUnit(e.target.value as ChangeUnit)}
              >
                <option value="percent">Percent change</option>
                <option value="absolute">Absolute change</option>
              </select>
            </label>

            <label className="field">
              <span className="label">
                Threshold * {changeUnit === "percent" ? "(%)" : "(absolute)"}
              </span>
              <input
                type="number"
                className="input"
                value={typeof trigger.threshold === "number" ? trigger.threshold : 0}
                onChange={(e) => updateTrigger({ threshold: Number(e.target.value) })}
                placeholder={changeUnit === "percent" ? "30" : "100"}
              />
              <div className="help" style={{ marginTop: 4 }}>
                Fires when <span className="mono">{trigger.column || "the selected column"}</span> for a
                given identity has changed more than this much since the previous evaluation.
              </div>
            </label>
          </>
        )}
      </div>

      {/* ── Dry-run ─────────────────────────────────────────────── */}
      <div className="card" style={{ background: "var(--bg-subtle)", display: "grid", gap: 8 }}>
        <div style={{ fontWeight: 600 }}>Test evaluation</div>
        <div className="help">
          Runs the watch against the current cached upstream result. No action items are created and no
          per-identity state is persisted.
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btnSm" type="button" onClick={() => void runDryRun()} disabled={busy}>
            {busy ? "Evaluating…" : "Evaluate now (dry run)"}
          </button>
          <button
            className="btn btnSm"
            type="button"
            onClick={() => void runDryRun(true)}
            disabled={busy}
            title="Re-evaluates, then emails you up to 3 rendered alerts, [TEST]-prefixed. Nothing is sent to the real recipients."
          >
            Email me a test
          </button>
        </div>
        {dryRunError && (
          <div className="help" style={{ color: "var(--danger-text)" }}>{dryRunError}</div>
        )}
        {dryRunResult && (
          <div className="card" style={{ background: "var(--bg-base, #fff)", display: "grid", gap: 6 }}>
            {dryRunResult.duplicate_identities_warning.length > 0 && (
              <div className="help" style={{ color: "var(--warning-text, #92400e)" }}>
                <strong>Duplicate identities found</strong> —{" "}
                {dryRunResult.duplicate_identities_warning.length} identity hash(es) appeared more than
                once this eval. Pick columns that uniquely identify each row, or ensure the upstream SQL
                returns unique rows.
              </div>
            )}
            <div>
              <strong>{dryRunResult.matched_rows.filter((r) => r.matches).length}</strong>{" "}
              {dryRunResult.matched_rows.filter((r) => r.matches).length === 1 ? "identity" : "identities"}{" "}
              matched ({dryRunResult.matched_rows.filter((r) => r.would_fire).length} would fire).
            </div>
            {dryRunResult.matched_rows.length > 0 && (
              <div className="tableWrap" style={{ maxHeight: 220, overflow: "auto" }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Identity</th>
                      <th>Matches</th>
                      <th>Would fire</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dryRunResult.matched_rows.slice(0, 20).map((r, i) => (
                      <tr key={i}>
                        <td className="mono" style={{ fontSize: 12 }}>
                          {Object.entries(r.identifying_values)
                            .map(([k, v]) => `${k}=${String(v)}`)
                            .join(" · ")}
                        </td>
                        <td>{r.matches ? "✓" : "—"}</td>
                        <td>{r.would_fire ? "✓" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Rendered previews (2026-08-17): what each fire would PRODUCE.
                A broken template shows its literal {{token}} here instead of
                shipping in a live alert — the 13-item incident, made visible
                one step earlier. */}
            {(dryRunResult.previews ?? []).length > 0 && (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  What would be created
                  {(dryRunResult.previews ?? []).length <
                    dryRunResult.matched_rows.filter((r) => r.would_fire).length && (
                    <span className="muted" style={{ fontWeight: 400 }}>
                      {" "}
                      (first {(dryRunResult.previews ?? []).length} of{" "}
                      {dryRunResult.matched_rows.filter((r) => r.would_fire).length})
                    </span>
                  )}
                </div>
                {(dryRunResult.previews ?? []).map((p) =>
                  p.responses.map((resp) =>
                    resp.previewable ? (
                      <div
                        key={`${p.identity_hash}:${resp.response_id}`}
                        className="card"
                        style={{ background: "var(--bg-subtle)", display: "grid", gap: 4, padding: 10 }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 12 }}>{resp.email_subject ?? resp.title}</div>
                        {resp.recipients && resp.recipients.length > 0 && (
                          <div className="muted" style={{ fontSize: 12 }}>
                            To: {resp.recipients.map((rc) => `${rc.name} (${rc.roles.join("/")})`).join(", ")}
                          </div>
                        )}
                        {resp.email_body && (
                          <div style={{ fontSize: 12, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                            {resp.email_body}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div key={`${p.identity_hash}:${resp.response_id}`} className="help">
                        {resp.note ?? `${resp.response_type} response — no preview`}
                      </div>
                    )
                  )
                )}
              </div>
            )}
          </div>
        )}
        <div className="help">
          <Link
            to={`/app/monitor/watches?operation_id=${encodeURIComponent(operationId)}&watch_node_id=${encodeURIComponent(selectedNode.id)}`}
          >
            View this watch's evaluation history →
          </Link>
        </div>
      </div>

      {/* ── ADR-0075 — Dynamic user mapping ─────────────────────── */}
      <DynamicUserMappingCard
        config={config}
        schema={schema}
        tenantHasCustomers={tenantHasCustomers}
        defaultHasPrimary={defaultHasPrimary}
        onChange={(patch) => updateConfig(patch)}
      />

      {/* ── Responses ───────────────────────────────────────────── */}
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontWeight: 700 }}>Responses</div>
          {config.dynamic_user_mapping_enabled && config.customer_column && (
            <span
              title="Dynamic user mapping is enabled on this watch."
              style={{
                fontSize: 12,
                padding: "2px 8px",
                borderRadius: 4,
                background: "var(--surface-soft)",
                border: "1px solid var(--border)",
                color: "var(--text-muted, var(--text))",
                fontWeight: 500
              }}
            >
              dynamic mapping ·{" "}
              <span className="mono">{config.customer_column}</span>
            </span>
          )}
        </div>

        {extraLoadError && (
          <div className="help" style={{ color: "var(--danger-text)" }}>
            Could not load codes / policies / directory: {extraLoadError}
          </div>
        )}

        {responses.length === 0 && (
          <div className="muted" style={{ fontStyle: "italic", fontSize: 13 }}>
            No responses yet. A watch needs at least one response to publish.
          </div>
        )}

        {responses.map((r, idx) => (
          <ResponseCard
            key={r.id}
            index={idx}
            config={r}
            candidates={candidates}
            itemTypes={itemTypes}
            policies={policies}
            dynamicMappingEnabled={config.dynamic_user_mapping_enabled === true}
            customerColumn={config.customer_column ?? null}
            customerRoleTypes={customerRoleTypes}
            onChange={(next) => {
              const copy = responses.slice();
              copy[idx] = next;
              setResponses(copy);
            }}
            onRemove={() => {
              const copy = responses.slice();
              copy.splice(idx, 1);
              setResponses(copy);
            }}
          />
        ))}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btnSm" type="button" onClick={() => addResponse("action_item")}>
            + Action item
          </button>
          <button className="btn btnSm" type="button" onClick={() => addResponse("email")}>
            + Email
          </button>
          <button className="btn btnSm" type="button" onClick={() => addResponse("webhook")}>
            + Webhook
          </button>
        </div>
      </div>

      {/* ── Tracking (ADR-0168) ─────────────────────────────────── */}
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontWeight: 700 }}>Tracking</div>
          <button
            className="btn btnSm"
            type="button"
            style={{ marginLeft: "auto" }}
            onClick={() => void resetTracking(null)}
            disabled={resetBusy || trackingLoading || (tracking?.summary.tracked ?? 0) === 0}
          >
            Reset all tracking…
          </button>
        </div>

        {trackingError && <ErrorState detail={trackingError} />}
        {trackingLoading && !trackingError && <LoadingState />}

        {!trackingLoading && !trackingError && tracking && (
          <>
            {tracking.summary.tracked === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>
                Not tracking any rows. Rows appear here after the watch evaluates and finds matches.
              </div>
            ) : (
              <>
                <div className="muted" style={{ fontSize: 13 }}>
                  Tracking {tracking.summary.tracked}{" "}
                  {tracking.summary.tracked === 1 ? "row" : "rows"} — {tracking.summary.matching}{" "}
                  currently matching, {tracking.summary.fired} have fired.
                </div>
                {tracking.currently_matching.length > 0 && (
                  <div className="tableWrap">
                    <table className="table tableCompact">
                      <thead>
                        <tr>
                          {identifyingColumns.map((col) => (
                            <th key={col}>{col}</th>
                          ))}
                          <th>Matching since</th>
                          <th>Last fired</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {tracking.currently_matching.map((row) => (
                          <tr key={row.identity_hash}>
                            {identifyingColumns.map((col) => {
                              const text = formatCellValue(row.identifying_values[col]);
                              return (
                                <td key={col} title={text}>
                                  {text}
                                </td>
                              );
                            })}
                            <td className="muted">{formatTimestamp(row.first_true_at)}</td>
                            <td className="muted">{formatTimestamp(row.last_fired_at)}</td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                className="adminListAction isDanger"
                                type="button"
                                onClick={() => void resetTracking(row)}
                                disabled={resetBusy}
                              >
                                Reset
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
            ),
          },
        ]}
      />
    </div>
  );
}

/**
 * Find `{{token}}` references in `template` whose lowercased name isn't in
 * `validKeys` (which is itself lowercased). Returns the original-cased token
 * names for display. De-dupes case-insensitively. Server-side interpolation
 * is case-insensitive (see watchConditions/interpolate.ts), so we mirror that
 * here — `{{Customer}}` resolves to a `Customer` column just fine.
 */
/** A tracked row's column value as text. Null reads as an em dash, never "null". */
function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Absolute local time, always — never "3 hours ago". */
function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function unresolvedTemplateRefs(template: string, validKeys: Set<string>): string[] {
  if (!template) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  const re = /\{\{(\w+)\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(template)) !== null) {
    const raw = match[1];
    const lk = raw.toLowerCase();
    if (validKeys.has(lk)) continue;
    if (seen.has(lk)) continue;
    seen.add(lk);
    out.push(raw);
  }
  return out;
}

function ResponseCard({
  index,
  config,
  candidates,
  itemTypes,
  policies,
  dynamicMappingEnabled,
  customerColumn,
  customerRoleTypes,
  onChange,
  onRemove
}: {
  index: number;
  config: ResponseConfig;
  candidates: MemberCandidate[];
  itemTypes: ActionItemCode[];
  policies: EscalationPolicy[];
  dynamicMappingEnabled?: boolean;
  customerColumn?: string | null;
  customerRoleTypes?: TenantCustomerRoleType[];
  onChange: (next: ResponseConfig) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="card" style={{ background: "var(--bg-subtle)", display: "grid", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          className="btn btnSm"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{ minWidth: 28 }}
        >
          {expanded ? "−" : "+"}
        </button>
        <div style={{ fontWeight: 600 }}>
          {index + 1}. {summarizeResponse(config)}
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button className="btn btnSm btnDanger" type="button" onClick={onRemove}>
            Remove
          </button>
        </div>
      </div>
      {expanded && (
        <>
          {config.type === "action_item" && (
            <ActionItemResponseForm
              config={config as ActionItemResponseConfig}
              candidates={candidates}
              itemTypes={itemTypes}
              policies={policies}
              onChange={onChange}
              dynamicMappingEnabled={dynamicMappingEnabled}
              customerColumn={customerColumn}
              customerRoleTypes={customerRoleTypes}
            />
          )}
          {config.type === "email" && (
            <EmailResponseForm
              config={config as EmailResponseConfig}
              candidates={candidates}
              onChange={onChange}
            />
          )}
          {config.type === "webhook" && (
            <WebhookResponseForm
              config={config as WebhookResponseConfig}
              onChange={onChange}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── ADR-0075 — Dynamic user mapping card ─────────────────────────────────
function DynamicUserMappingCard({
  config,
  schema,
  tenantHasCustomers,
  defaultHasPrimary,
  onChange
}: {
  config: WatchConfig;
  schema: InputSchema | null;
  tenantHasCustomers: boolean;
  defaultHasPrimary: boolean;
  onChange: (patch: Partial<WatchConfig>) => void;
}) {
  const enabled = config.dynamic_user_mapping_enabled === true;
  const customerCol = config.customer_column ?? "";
  const [open, setOpen] = useState(false);
  const columns = schema?.columns ?? [];
  const colNames = new Set(columns.map((c) => c.name));

  const issues: string[] = [];
  if (enabled && customerCol === "") {
    issues.push("Customer column is required when dynamic mapping is on.");
  }
  if (enabled && customerCol !== "" && schema && !colNames.has(customerCol)) {
    issues.push(
      `Column "${customerCol}" is not in the upstream schema. Re-run the upstream node, or pick another column.`
    );
  }
  if (enabled && !tenantHasCustomers) {
    issues.push("This tenant has no customers configured. Add at least one in admin → Customers.");
  }
  if (enabled && !defaultHasPrimary) {
    issues.push(
      "Default coverage has no primary user. Add one on the Customers page → Manage default coverage so unmatched account codes resolve."
    );
  }

  return (
    <div className="card" style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          className="btn btnSm"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? "−" : "+"}
        </button>
        <div style={{ fontWeight: 700 }}>Dynamic user mapping</div>
        <span className={`pill ${enabled ? "pillOk" : "pillNeutral"}`}>
          {enabled ? `On — column: ${customerCol || "(unset)"}` : "Off"}
        </span>
      </div>
      <div className="help">
        Resolve action item assignees from the customer in this row, instead of always using the
        explicit users below.
      </div>

      {open && (
        <>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => onChange({ dynamic_user_mapping_enabled: e.target.checked })}
            />
            <span>Enable dynamic user mapping for this watch.</span>
          </label>
          <label className="field">
            <span className="label">Customer column</span>
            <select
              className="select"
              value={customerCol}
              disabled={!enabled}
              onChange={(e) => onChange({ customer_column: e.target.value === "" ? null : e.target.value })}
            >
              <option value="">—</option>
              {columns.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name} ({c.type})
                </option>
              ))}
            </select>
            <div className="help muted">
              The column on each upstream row holding the customer's stable external ID. Picked
              from this watch's input schema.
            </div>
          </label>
          {issues.length > 0 && (
            <div className="card" style={{ background: "var(--surface-soft)" }}>
              {issues.map((i, idx) => (
                <div key={idx} className="help" style={{ color: "var(--warning-text, #92400e)" }}>
                  {i}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
